// PartBPage5/index.jsx - Part B Page 5 (Student Editor - Editable Reflection)
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useActivityContext } from '../index';
import { API_CONFIG } from '../../../../../config/api';

const PartBPage5 = () => {
  const { activity } = useActivityContext();
  const [formData, setFormData] = useState({
    learner_reflection: {
      awareness: [],
      sensitivity: [],
      creativity: []
    },
    peer_feedback: {
      awareness: [],
      sensitivity: [],
      creativity: []
    }
  });
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const learnerReflectionItems = {
    awareness: [
      "I could identify areas where my understanding of the project has improved.",
      "I could explain how my work contributed to the overall project.",
      "I was able to improve the project based on my own and my peers' review."
    ],
    sensitivity: [
      "I was able to build a positive emotional atmosphere within the group.",
      "I could reflect on my strengths and areas for improvement.",
      "I could understand the social relevance of the project."
    ],
    creativity: [
      "I was able to make creative contributions to the project.",
      "I was able to take initiative to complete the project.",
      "I was able to use different materials, tools, and resources during the project."
    ]
  };

  const peerFeedbackItems = {
    awareness: [
      "My peer showed improved understanding of the project.",
      "My peer could explain how her/his work contributed to the overall project.",
      "My peer was able to improve the project based on her/his own and their peers' review."
    ],
    sensitivity: [
      "My peer helped to build a positive emotional atmosphere within the group by valuing everyone's opinions.",
      "My peer could reflect on her/his strengths and areas for improvement.",
      "My peer could understand the social relevance of the project."
    ],
    creativity: [
      "My peer was able to make creative contributions to the project.",
      "My peer was able to take the initiative to help complete the project.",
      "My peer was able to use different materials, tools, and resources during the project."
    ]
  };

  useEffect(() => {
    if (activity?.my_response?.page_5) {
      const page5 = activity.my_response.page_5;
      setFormData({
        learner_reflection: page5.learner_reflection || {
          awareness: [],
          sensitivity: [],
          creativity: []
        },
        peer_feedback: page5.peer_feedback || {
          awareness: [],
          sensitivity: [],
          creativity: []
        }
      });
    }
  }, [activity]);

  const saveChanges = async (dataToSave) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('accessToken');
      const apiUrl = API_CONFIG.BASE_URL;

      await axios.put(
        `${apiUrl}/secondary/user/domain-activities/${activity.activity_id}/part-b/page/5/student-fields`,
        dataToSave,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSaveMessage('Saved ✓');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch (error) {
      console.error('Error saving:', error);
      setSaveMessage('Error saving');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleCheckboxChange = (section, category, index) => {
    const currentItems = formData[section][category] || [];
    const newItems = currentItems.includes(index)
      ? currentItems.filter(i => i !== index)
      : [...currentItems, index];

    const newFormData = {
      ...formData,
      [section]: {
        ...formData[section],
        [category]: newItems
      }
    };
    setFormData(newFormData);
    saveChanges(newFormData);
  };

  const renderCheckboxSection = (title, items, category, section, bgColor, headerBg) => {
    const checkedIndices = formData[section][category] || [];
    const total = checkedIndices.length;

    return (
      <div style={{ marginBottom: '12px' }}>
        <div style={{
          backgroundColor: headerBg,
          color: '#333',
          padding: '6px 12px',
          fontWeight: 'bold',
          fontSize: '14px',
          marginBottom: '6px'
        }}>
          {title}
        </div>
        <div style={{
          backgroundColor: bgColor,
          padding: '10px'
        }}>
          {items.map((item, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: '5px',
              fontSize: '14px',
              lineHeight: '1.3'
            }}>
              <input
                type="checkbox"
                checked={checkedIndices.includes(index)}
                onChange={() => handleCheckboxChange(section, category, index)}
                style={{
                  width: '16px',
                  height: '16px',
                  marginRight: '8px',
                  marginTop: '1px',
                  flexShrink: 0,
                  cursor: 'pointer'
                }}
              />
              <span>{item}</span>
            </div>
          ))}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginTop: '8px',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            <span style={{ marginRight: '8px' }}>Total number of (✓):</span>
            <div style={{
              width: '32px',
              height: '32px',
              border: '3px solid ' + (headerBg === '#6FA8DC' ? '#4A90A4' : '#DAA520'),
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '15px'
            }}>
              {total}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{
        width: '100%',
        maxWidth: '1400px',
        minHeight: '80vh',
        margin: '0 auto',
        padding: '40px',
        backgroundColor: 'white',
        fontFamily: "'Poppins', Arial, sans-serif",
        position: 'relative',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
      }}>
        {/* Save Status */}
        {saveMessage && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: saveMessage.includes('Error') ? '#f44336' : '#4caf50',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '4px',
            zIndex: 1000
          }}>
            {saveMessage}
          </div>
        )}

        {/* Decorative Corners */}
        

        {/* Header */}
        <div style={{
          backgroundColor: '#F4824C',
          color: 'white',
          fontSize: '14px',
          fontWeight: 'bold',
          padding: '10px',
          textAlign: 'center',
          marginBottom: '16px'
        }}>
          Stage 3
        </div>

        {/* Learner Reflection Section */}
        <div style={{
          backgroundColor: '#4A90A4',
          color: 'white',
          padding: '8px 14px',
          fontWeight: 'bold',
          fontSize: '12px',
          display: 'inline-block',
          marginBottom: '10px'
        }}>
          Learner Reflection
        </div>

        <div style={{
          fontSize: '14px',
          fontWeight: 'bold',
          marginBottom: '10px'
        }}>
          Tick (✓) all statements that apply.
        </div>

        {renderCheckboxSection('Awareness', learnerReflectionItems.awareness, 'awareness', 'learner_reflection', '#C5E3ED', '#6FA8DC')}
        {renderCheckboxSection('Sensitivity', learnerReflectionItems.sensitivity, 'sensitivity', 'learner_reflection', '#C5E3ED', '#6FA8DC')}
        {renderCheckboxSection('Creativity', learnerReflectionItems.creativity, 'creativity', 'learner_reflection', '#C5E3ED', '#6FA8DC')}

        {/* Peer Feedback Section */}
        <div style={{
          backgroundColor: '#DAA520',
          color: 'white',
          padding: '8px 14px',
          fontWeight: 'bold',
          fontSize: '12px',
          display: 'inline-block',
          marginBottom: '10px',
          marginTop: '10px'
        }}>
          Peer Feedback
        </div>

        <div style={{
          fontSize: '14px',
          fontWeight: 'bold',
          marginBottom: '10px'
        }}>
          Tick (✓) all statements that apply.
        </div>

        {renderCheckboxSection('Awareness', peerFeedbackItems.awareness, 'awareness', 'peer_feedback', '#FFF4CC', '#F4D03F')}
        {renderCheckboxSection('Sensitivity', peerFeedbackItems.sensitivity, 'sensitivity', 'peer_feedback', '#FFF4CC', '#F4D03F')}
        {renderCheckboxSection('Creativity', peerFeedbackItems.creativity, 'creativity', 'peer_feedback', '#FFF4CC', '#F4D03F')}

        {/* Bottom Corners */}
        
        
      </div>

  );
};

export default PartBPage5;
